import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import {
  AlertTriangle,
  ArrowLeft,
  Play,
  Shield,
  Target,
  Trophy,
} from 'lucide-react';
import './CCACandidate.css';
import {
  useCompleteCCAAssessmentMutation,
  useLazyGetCCACurrentQuestionQuery,
  useStartCCAAssessmentMutation,
  useSubmitCCAAnswerMutation,
  useValidateCCATokenQuery,
} from '../../utils/api_service/ccaCandidateApi';
import {
  CCACandidateQuestion,
  CCATokenValidationResponse,
} from '../../utils/types/cca_candidate_types';
import { QuestionScreen } from './QuestionScreen';
import { TankQuestionScreen } from './TankQuestionScreen';
import GameSelector, { GameTheme } from './GameSelector';

type Phase =
  | 'game_select'
  | 'loading'
  | 'error'
  | 'consent'
  | 'starting'
  | 'question'
  | 'completing'
  | 'complete';

// ─── Background layers (shared across all screens) ────────────────────────────

const Background: React.FC = () => (
  <>
    <Box className='cca-sky' />
    <Box className='cca-clouds'>
      <span className='cca-cloud cca-cloud--1' style={{ opacity: 0.6 }}>☁</span>
      <span className='cca-cloud cca-cloud--2' style={{ opacity: 0.6 }}>☁</span>
      <span className='cca-cloud cca-cloud--3' style={{ opacity: 0.6 }}>☁</span>
    </Box>
    <Box className='cca-trees-bg'>
      {['🌲', '🌳', '🌲', '🌳', '🌲', '🌳', '🌲'].map((t, i) => (
        <span
          key={i}
          style={{
            fontSize: `${1.8 + (i % 3) * 0.5}rem`,
            opacity: 0.65 + (i % 2) * 0.25,
          }}
        >
          {t}
        </span>
      ))}
    </Box>
    <Box className='cca-grass' />
  </>
);

// ─── Tank background (desert) ────────────────────────────────────────────────

const TankBg: React.FC = () => (
  <>
    <Box className='cca-sky cca-sky--tank' />
    <Box className='cca-tank-sun' aria-hidden='true' />
    <Box className='cca-tank-mountains' aria-hidden='true' />
    <Box className='cca-tank-dunes' aria-hidden='true' />
    <Box className='cca-grass cca-grass--tank' />
  </>
);

// ─── CCA Assessment Page ──────────────────────────────────────────────────────

export const CCAAssessmentPage: React.FC = () => {
  const search = useLocation().search;
  const token = new URLSearchParams(search).get('token') ?? '';

  const [phase, setPhase] = useState<Phase>('game_select');
  const [gameTheme, setGameTheme] = useState<GameTheme>('archer');
  const [errorMsg, setErrorMsg] = useState('');
  const [sessionInfo, setSessionInfo] =
    useState<CCATokenValidationResponse | null>(null);
  const [currentQuestion, setCurrentQ] =
    useState<CCACandidateQuestion | null>(null);
  const [assignedIds, setAssignedIds] = useState<string[]>([]);
  const [answeredCount, setAnsweredCount] = useState(0);

  const questionSeqRef = useRef(0);
  const handledRef = useRef(false);

  const { data: tokenData, error: tokenError, isLoading: tokenLoading } =
    useValidateCCATokenQuery(token, { skip: !token });

  const [startAssessment] = useStartCCAAssessmentMutation();
  const [fetchQuestion] = useLazyGetCCACurrentQuestionQuery();
  const [submitAnswer] = useSubmitCCAAnswerMutation();
  const [completeAssessment] = useCompleteCCAAssessmentMutation();

  // Fetch next question and advance the state machine
  const loadNextQuestion = useCallback(
    async (tok: string) => {
      const seq = questionSeqRef.current;
      questionSeqRef.current += 1;

      const result = await fetchQuestion({ token: tok, seq }, false);
      if ('error' in result || !result.data) {
        setErrorMsg('Failed to load question. Please refresh the page.');
        setPhase('error');
        return;
      }
      const data = result.data;
      if ('done' in data && data.done) {
        setCurrentQ(null);
        setPhase('completing');
        try {
          await completeAssessment(tok).unwrap();
        } catch {
          // show complete screen regardless
        }
        setPhase('complete');
      } else {
        const q = data as CCACandidateQuestion;
        setAnsweredCount(q.questionNumber - 1);
        setCurrentQ(q);
        setPhase('question');
      }
    },
    [fetchQuestion, completeAssessment],
  );

  // Start or resume the assessment
  const beginFlow = useCallback(async () => {
    if (!token) return;
    setPhase('starting');
    try {
      const startResult = await startAssessment(token).unwrap();
      setAssignedIds(startResult.assignedQuestionIds ?? []);
      await loadNextQuestion(token);
    } catch (e: any) {
      const detail: string = e?.data?.detail || e?.data?.message || '';
      setErrorMsg(detail || 'Failed to start the assessment. Please try again.');
      setPhase('error');
    }
  }, [token, startAssessment, loadNextQuestion]);

  // React to token validation result (runs once — but waits until user has picked a game)
  useEffect(() => {
    if (handledRef.current) return;
    if (phase === 'game_select') return; // wait until game is chosen

    if (!token) {
      handledRef.current = true;
      setErrorMsg('No assessment token found. Please use the link from your email.');
      setPhase('error');
      return;
    }
    if (tokenLoading) return;

    if (tokenError) {
      handledRef.current = true;
      const err = tokenError as any;
      const detail: string = err?.data?.detail || err?.data?.message || '';
      if (detail.toLowerCase().includes('expired')) {
        setErrorMsg('Your assessment link has expired. Please contact the recruiter.');
      } else if (detail.toLowerCase().includes('completed')) {
        setErrorMsg('You have already completed this assessment.');
      } else {
        setErrorMsg('Invalid or malformed link. Please use the link from your email.');
      }
      setPhase('error');
      return;
    }

    if (tokenData) {
      handledRef.current = true;
      setSessionInfo(tokenData);
      if (tokenData.startedAt !== null) {
        // Resume in-progress assessment — skip consent screen
        beginFlow();
      } else {
        setPhase('consent');
      }
    }
  }, [phase, tokenLoading, tokenError, tokenData, beginFlow, token]);

  // Called by QuestionScreen after animation completes
  const handleAnswer = useCallback(
    async (
      selectedOptionId: string | null,
      shortAnswerText: string | null,
      timeTaken: number,
    ) => {
      if (!currentQuestion || !token) return;

      const questionId = currentQuestion.configQuestionId;
      setCurrentQ(null); // show loading while fetching next

      try {
        await submitAnswer({
          token,
          body: {
            config_question_id: questionId,
            selected_option_id: selectedOptionId,
            short_answer_text: shortAnswerText,
            time_taken_seconds: timeTaken,
          },
        }).unwrap();
      } catch (e: any) {
        const detail: string = e?.data?.detail || '';
        if (!detail.includes('already answered')) {
          console.error('Answer submit error:', e);
        }
      }

      await loadNextQuestion(token);
    },
    [currentQuestion, token, submitAnswer, loadNextQuestion],
  );

  const expiresDate = sessionInfo
    ? new Date(sessionInfo.expiresAt * 1000)
    : null;
  const formattedExpiry = expiresDate
    ? expiresDate.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    : '';

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (phase === 'game_select') {
    return (
      <GameSelector
        onSelect={(theme) => {
          setGameTheme(theme);
          setPhase('loading');
        }}
      />
    );
  }

  const isTank = gameTheme === 'tank';

  return (
    <Box className={`cca ${isTank ? 'cca--tank' : ''}`}>
      {isTank ? <TankBg /> : <Background />}

      {/* Header */}
      <Box component='header' className='cca-hdr'>
        <Box className='cca-hdr-brand'>
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {isTank ? <Shield size={18} /> : <Target size={18} />}
          </span>
          <span className='cca-brand-full'>
            {isTank ? ' Tank Commander Assessment' : ' Cognitive Assessment'}
          </span>
          <span className='cca-brand-short'> Assessment</span>
        </Box>
        {phase === 'question' && currentQuestion && (
          <Box className='cca-hdr-stats'>
            <span className='cca-badge'>
              Q {currentQuestion.questionNumber} / {currentQuestion.totalQuestions}
            </span>
            <span className='cca-badge cca-badge--subdomain'>
              {currentQuestion.subdomain.replace(/_/g, ' ')}
            </span>
          </Box>
        )}
      </Box>

      {/* ── Loading ── */}
      {(phase === 'loading' || phase === 'starting') && (
        <Box className='cca-content'>
          <Box className='cca-center-card'>
            <Box className='cca-spinner' />
            <p className='cca-loading-text'>
              {phase === 'starting'
                ? 'Preparing your assessment…'
                : 'Verifying your link…'}
            </p>
          </Box>
        </Box>
      )}

      {/* ── Completing ── */}
      {phase === 'completing' && (
        <Box className='cca-content'>
          <Box className='cca-center-card'>
            <Box className='cca-spinner' />
            <p className='cca-loading-text'>Saving your responses…</p>
          </Box>
        </Box>
      )}

      {/* ── Error ── */}
      {phase === 'error' && (
        <Box className='cca-content'>
          <Box className='cca-center-card cca-error-card'>
            <Box className='cca-error-icon' sx={{ color: '#ef4444' }}>
              <AlertTriangle size={36} />
            </Box>
            <h2 className='cca-error-title'>Assessment Unavailable</h2>
            <p className='cca-error-msg'>{errorMsg}</p>
          </Box>
        </Box>
      )}

      {/* ── Consent ── */}
      {phase === 'consent' && sessionInfo && (
        <Box className='cca-content'>
          <Box className='cca-consent-card'>
            <Box className='cca-consent-icon'>
              {isTank ? <Shield size={36} color='#fbbf24' /> : <Target size={36} color='#10b981' />}
            </Box>
            <h1 className='cca-consent-title'>Cognitive Assessment</h1>
            <p className='cca-consent-subtitle'>
              {isTank ? 'Tank Commander Mode' : 'Robinhood Archer Mode'}
              {sessionInfo.attemptNumber > 1 &&
                ` — Attempt #${sessionInfo.attemptNumber}`}
            </p>

            <Box className='cca-consent-stats'>
              <Box className='cca-stat'>
                <span className='cca-stat-val'>{sessionInfo.totalQuestions}</span>
                <span className='cca-stat-label'>Questions</span>
              </Box>
              <Box className='cca-stat-divider' />
              <Box className='cca-stat'>
                <span className='cca-stat-val'>
                  {Math.round(sessionInfo.totalTimeSeconds / 60)}
                </span>
                <span className='cca-stat-label'>Minutes</span>
              </Box>
            </Box>

            <Box className='cca-consent-rules'>
              <p>• Each question has its own time limit — answer before it runs out</p>
              <p>• Answers cannot be changed once submitted</p>
              <p>• Do not close or refresh this tab during the assessment</p>
              <p>• If you lose connection, return via the same email link to resume</p>
            </Box>

            {formattedExpiry && (
              <p className='cca-consent-expiry'>Link expires: {formattedExpiry}</p>
            )}

            <button className='cca-begin-btn' onClick={beginFlow}>
              <Play size={16} style={{ display: 'inline', marginRight: 6 }} />
              Begin Assessment
            </button>

            <button
              className='cca-back-game-btn'
              onClick={() => {
                handledRef.current = false;
                setPhase('game_select');
              }}
            >
              <ArrowLeft size={14} style={{ display: 'inline', marginRight: 4 }} />
              Change game
            </button>
          </Box>
        </Box>
      )}

      {/* ── Question (loading next) ── */}
      {phase === 'question' && !currentQuestion && (
        <Box className='cca-content'>
          <Box className='cca-q-loading'>
            <Box className='cca-spinner' />
            <p className='cca-q-loading-text'>Loading next question…</p>
          </Box>
        </Box>
      )}

      {/* ── Question (active) ── */}
      {phase === 'question' &&
        currentQuestion &&
        (isTank ? (
          <TankQuestionScreen
            key={currentQuestion.configQuestionId}
            token={token}
            question={currentQuestion}
            totalAssigned={
              (assignedIds ?? []).length || currentQuestion.totalQuestions
            }
            answeredCount={answeredCount}
            onAnswer={handleAnswer}
          />
        ) : (
          <QuestionScreen
            key={currentQuestion.configQuestionId}
            token={token}
            question={currentQuestion}
            totalAssigned={
              (assignedIds ?? []).length || currentQuestion.totalQuestions
            }
            answeredCount={answeredCount}
            onAnswer={handleAnswer}
          />
        ))}

      {/* ── Complete ── */}
      {phase === 'complete' && (
        <Box className='cca-content'>
          <Box className='cca-complete-card'>
            <Box className='cca-complete-icon' sx={{ color: '#f59e0b' }}>
              <Trophy size={40} />
            </Box>
            <h1 className='cca-complete-title'>Thank You!</h1>
            <p className='cca-complete-msg'>
              Your responses have been recorded and will be reviewed by the
              recruiting team shortly.
            </p>
            <p className='cca-complete-sub'>You may now close this window.</p>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CCAAssessmentPage;
